import { RANGE_SEPARATOR } from '@localization/formatters/dateFormatter';

const NBSP = '\u00A0';
// U+202F is U+2009's non-breaking twin, same width. ICU emits thin spaces inside dates in several locales
// (en-GB "12 September"), so swapping those for a plain NBSP would visibly widen the gap in those locales.
const THIN_SPACE = '\u2009';
const NARROW_NBSP = '\u202F';

// Leaves a formatted date range with exactly one place it can break: the space *after* the separator.
//
// A range is long enough to wrap on every surface that renders one — a ~390px guest landing, a 375px admin
// editor column — and left to the browser it breaks wherever it runs out of width, which orphans whatever
// trails the last space ("Thursday, March 11 - Friday, March 12, / 2027"). Holding every other space makes
// the separator the only candidate, so the two endpoints stay whole:
//
//   Thursday, March 11 -
//   Friday, March 12, 2027
//
// This offers the break rather than forcing it: a range that fits still renders on one line, so short forms
// ("09/12/2026 - 10/04/2026") aren't gratuitously stacked.
//
// All whitespace is held, not just U+0020: ICU mixes thin spaces into date patterns (en-GB, de-DE, fr-FR) and
// those are breakable too, so matching only ASCII spaces would leave a stray break point mid-endpoint.
//
// Returns the input unchanged when there's no separator to break at — a same-day range collapses to a single
// date, and a locale whose separator ICU doesn't hand us in this form simply keeps its natural wrapping.
//
// Display only. Never apply this to a value that gets persisted, logged, or compared: the non-breaking spaces
// are invisible but not equal to ASCII ones, so they would fragment an analytics field or break an equality
// check downstream (see `canonicalEn` in useEventLevelDateDisplay).
export const withSingleRangeBreakPoint = (formattedRange: string): string => {
  const separatorIndex = formattedRange.indexOf(RANGE_SEPARATOR);
  if (separatorIndex === -1) return formattedRange;
  const holdTogether = (segment: string) => segment.replace(/\s/g, space => (space === THIN_SPACE || space === NARROW_NBSP ? NARROW_NBSP : NBSP));
  const start = holdTogether(formattedRange.slice(0, separatorIndex));
  const end = holdTogether(formattedRange.slice(separatorIndex + RANGE_SEPARATOR.length));
  return `${start}${holdTogether(RANGE_SEPARATOR.trimEnd())} ${end}`;
};
